import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { getAgreementVersion, getAgreementContent, submitCookieConsentLog } from '@/api/requests/CookieRequests'

export type ConsentType = 'necessary' | 'all'

interface ConsentRecord {
  type: ConsentType
  version: string
  timestamp: number
  isUpdate: boolean
  presentationUrl: string
  operationType: string
}

const COOKIE_CONSENT_KEY = 'COOKIE_CONSENT_INFO_H5'
const VERSION_CHECK_KEY = 'COOKIE_VERSION_CHECK_DATE'
const MAX_DAILY_CALLS = 5

function getCookiePolicyCode(): string {
  return process.env.VUE_APP_COOKIE_POLICY_CODE || ''
}

function getConsentRecord(): ConsentRecord | null {
  const raw = localStorage.getItem(COOKIE_CONSENT_KEY)
  if (!raw) return null
  try {
    const record = JSON.parse(raw) as ConsentRecord
    if (record && record.type && record.timestamp) {
      return record
    }
    return null
  } catch {
    return null
  }
}

function saveConsentRecord(type: ConsentType, version: string, isUpdate: boolean, presentationUrl: string, operationType: string) {
  localStorage.setItem(
    COOKIE_CONSENT_KEY,
    JSON.stringify({ type, version, timestamp: Date.now(), isUpdate, presentationUrl, operationType })
  )
}

// 每日调用次数限制（协议更新推送查询接口要求每天不超过5次）
function canCallVersionApi(): boolean {
  const today = new Date().toISOString().split('T')[0]
  const stored = localStorage.getItem(VERSION_CHECK_KEY)
  if (!stored) return true
  try {
    const record = JSON.parse(stored) as { date: string; count: number }
    if (record.date !== today) return true
    return record.count < MAX_DAILY_CALLS
  } catch {
    return true
  }
}

function recordVersionApiCall() {
  const today = new Date().toISOString().split('T')[0]
  const stored = localStorage.getItem(VERSION_CHECK_KEY)
  let count = 1
  if (stored) {
    try {
      const record = JSON.parse(stored) as { date: string; count: number }
      count = record.date === today ? record.count + 1 : 1
    } catch {
      count = 1
    }
  }
  localStorage.setItem(VERSION_CHECK_KEY, JSON.stringify({ date: today, count }))
}

// 语义化版本号比较：返回 -1(v1<v2) / 0(相等) / 1(v1>v2)
function compareVersions(v1: string, v2: string): number {
  const parts1 = v1.split('.').map(n => parseInt(n, 10) || 0)
  const parts2 = v2.split('.').map(n => parseInt(n, 10) || 0)
  const maxLen = Math.max(parts1.length, parts2.length)
  for (let i = 0; i < maxLen; i++) {
    const a = parts1[i] || 0
    const b = parts2[i] || 0
    if (a < b) return -1
    if (a > b) return 1
  }
  return 0
}

const needConsent = ref(false)

export function useCookieConsent() {
  const { t } = useI18n()
  const cachedRecord = getConsentRecord()
  const loading = ref(false)
  const consentError = ref('')
  const isUpdate = ref(false)
  const presentationUrl = ref(cachedRecord?.presentationUrl || '')

  // 用户是否访问过登录页（用于区分登录 vs 免登录）
  const visitedLoginPage = ref(false)

  async function checkConsentStatus(): Promise<boolean> {
    const policyCode = getCookiePolicyCode()
    if (!policyCode) {
      needConsent.value = false
      return false
    }

    loading.value = true
    consentError.value = ''
    try {
      const record = getConsentRecord()

      if (!record) {
        // 无缓存：调 queryConsultativeByCode 初始化，不调 queryLatest
        const contentRes = await getAgreementContent(policyCode)
        presentationUrl.value = contentRes.presentationUrl || ''
        isUpdate.value = false
        needConsent.value = true
        return true
      }

      // 有缓存：检查每日调用次数限制
      if (!canCallVersionApi()) {
        presentationUrl.value = record.presentationUrl || ''
        needConsent.value = false
        return false
      }

      // 调 queryLatest 检查版本更新
      const versionRes = await getAgreementVersion(policyCode)
      recordVersionApiCall()

      const currentVersion = versionRes.version
      presentationUrl.value = versionRes.presentationUrl || ''

      if (compareVersions(record.version, currentVersion) < 0) {
        // 本地版本 < 服务端最新版本 -> 判定协议更新，触发更新弹窗
        isUpdate.value = true
        needConsent.value = true
        return true
      }

      // 版本一致，无需弹窗
      needConsent.value = false
      return false
    } catch {
      // 网络异常：已有记录则允许继续，否则降级使用必要Cookie
      const record = getConsentRecord()
      if (!record) {
        consentError.value = t('Cookie设置可能未保存，请稍后重试')
      } else {
        presentationUrl.value = record.presentationUrl || ''
      }
      needConsent.value = false
      return false
    } finally {
      loading.value = false
    }
  }

  async function acceptConsent(type: ConsentType) {
    const policyCode = getCookiePolicyCode()
    if (!policyCode) return

    loading.value = true
    consentError.value = ''
    try {
      const record = getConsentRecord()
      let version: string
      let url: string

      if (!record) {
        // 无缓存：调 queryConsultativeByCode 获取版本
        const contentRes = await getAgreementContent(policyCode)
        version = contentRes.version || ''
        url = contentRes.presentationUrl || ''
      } else {
        // 有缓存：调 queryLatest 获取最新版本
        const versionRes = await getAgreementVersion(policyCode)
        version = versionRes.version
        url = versionRes.presentationUrl || ''
      }

      presentationUrl.value = url
      const operationType = visitedLoginPage.value ? '登录' : '免登录进来'
      saveConsentRecord(type, version, isUpdate.value, url, operationType)

      needConsent.value = false
      isUpdate.value = false
    } catch {
      consentError.value = t('Cookie设置可能未保存，请稍后重试')
      needConsent.value = false
    } finally {
      loading.value = false
    }
  }

  // 登录成功后调用：检查本地有同意记录才上报
  async function submitPendingConsentLog(userIdentifier: string) {
    const record = getConsentRecord()
    if (!record) return

    const policyCode = getCookiePolicyCode()
    if (!policyCode) return

    try {
      await submitCookieConsentLog({
        consultativeName: userIdentifier,
        consultativeCode: policyCode,
        consultativeVersion: record.version,
        operationType: record.isUpdate ? '更新弹窗' : (record.operationType || '免登录进来')
      })
    } catch {
      // 上报失败不阻塞用户
    }
  }

  return {
    needConsent,
    loading,
    consentError,
    isUpdate,
    presentationUrl,
    visitedLoginPage,
    checkConsentStatus,
    acceptConsent,
    submitPendingConsentLog
  }
}
